import type { BeforeActionAllowlist, TourPlacement } from './types';
import { AMERICON_MARK } from '../branding/americon.js';

export type NavReferenceVisibility =
  | 'always'
  | 'auth'
  | 'guest'
  | 'desktop'
  | 'mobile';

export type NavReferenceBeforeAction =
  | BeforeActionAllowlist
  | 'closeNavDropdowns'
  | 'closeNavDropdownsAndScroll'
  | 'openNavBrowseDropdown'
  | 'openNavAboutDropdown'
  | 'openNavProductsDropdown'
  | 'openNavUserMenu';

export interface NavReferenceStepSpec {
  stepName: string;
  tourAnchor: string;
  useStableAnchor?: boolean;
  title: string;
  content: string;
  visibility: NavReferenceVisibility;
  beforeAction?: NavReferenceBeforeAction;
  placement?: TourPlacement;
  /** Dropdown menu items are portaled; skip DOM presence at filter time. */
  isDropdownItem?: boolean;
}

export function navReferenceTarget(anchor: string, useStable = false): string {
  return useStable
    ? `[data-tour-stable="${anchor}"]`
    : `[data-tour="${anchor}"]`;
}

function isElementVisible(el: Element): boolean {
  const view = el.ownerDocument?.defaultView;
  if (!view?.getComputedStyle) {
    return true;
  }
  const style = view.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

export function isDesktopHeaderNavVisible(root: ParentNode = document): boolean {
  const el = root.querySelector('[data-tour="header-nav"]');
  if (!el) return false;
  return isElementVisible(el as Element);
}

export function isMobileHeaderNavVisible(root: ParentNode = document): boolean {
  const el = root.querySelector('[data-tour-stable="nav-mobile-menu"]');
  if (!el) return false;
  return isElementVisible(el as Element);
}

export function isAuthenticatedNavVisible(root: ParentNode = document): boolean {
  return root.querySelector('[data-tour-stable="nav-user-menu"]') !== null;
}

export function isGuestNavVisible(root: ParentNode = document): boolean {
  return root.querySelector('[data-tour-stable="nav-about-us-button"]') !== null;
}

function navAnchorExists(
  anchor: string,
  useStable: boolean,
  root: ParentNode,
): boolean {
  if (useStable) {
    return root.querySelector(`[data-tour-stable="${anchor}"]`) !== null;
  }
  return (
    root.querySelector(`[data-tour="${anchor}"]`) !== null ||
    root.querySelector(`[data-tour-stable="${anchor}"]`) !== null
  );
}

export function shouldSkipNavReferenceStep(
  spec: NavReferenceStepSpec,
  root: ParentNode = document,
): boolean {
  switch (spec.visibility) {
    case 'auth':
      if (!isAuthenticatedNavVisible(root)) return true;
      break;
    case 'guest':
      if (!isGuestNavVisible(root)) return true;
      break;
    case 'desktop':
      if (!isDesktopHeaderNavVisible(root)) return true;
      break;
    case 'mobile':
      if (!isMobileHeaderNavVisible(root)) return true;
      break;
    default:
      break;
  }

  if (spec.isDropdownItem) {
    return false;
  }

  return !navAnchorExists(
    spec.tourAnchor,
    spec.useStableAnchor ?? false,
    root,
  );
}

const BROWSE_DROPDOWN_ITEMS: ReadonlyArray<{
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
}> = [
  {
    stepName: 'nav_browse_all_events',
    tourAnchor: 'nav-browse-all-events',
    title: 'All Events',
    content: 'Browse upcoming training events across agencies and regions.',
  },
  {
    stepName: 'nav_browse_agencies',
    tourAnchor: 'nav-browse-agencies',
    title: 'Agencies',
    content: 'Explore participating law enforcement agencies.',
  },
  {
    stepName: 'nav_browse_venues',
    tourAnchor: 'nav-browse-venues',
    title: 'Venues',
    content: 'Discover training locations and facility details.',
  },
  {
    stepName: 'nav_browse_course_catalog',
    tourAnchor: 'nav-browse-course-catalog',
    title: 'Course Catalog',
    content: 'Browse the full catalog of training courses.',
  },
  {
    stepName: 'nav_browse_people',
    tourAnchor: 'nav-browse-people',
    title: 'People',
    content: 'Find professionals, instructors, and authors on APT.',
  },
  {
    stepName: 'nav_browse_photo_gallery',
    tourAnchor: 'nav-browse-photo-gallery',
    title: 'Photo Gallery',
    content: 'View training and community photo collections.',
  },
  {
    stepName: 'nav_browse_leo_restaurants',
    tourAnchor: 'nav-browse-leo-recommended-restaurants',
    title: 'LEO Recommended Restaurants',
    content: 'Officer-recommended dining options near training sites.',
  },
  {
    stepName: 'nav_browse_secondary_employment',
    tourAnchor: 'nav-browse-secondary-employment',
    title: 'Secondary Employment',
    content: 'Explore secondary employment opportunities for officers.',
  },
  {
    stepName: 'nav_browse_knowledge_base',
    tourAnchor: 'nav-browse-knowledge-base',
    title: 'Knowledge Base',
    content: 'Reference guides, policies, and training resources.',
  },
];

const PRODUCTS_DROPDOWN_ITEMS: ReadonlyArray<{
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
}> = [
  {
    stepName: 'nav_products_tacticore',
    tourAnchor: 'nav-products-tacticore',
    title: 'TactiCore™',
    content: 'Access TactiCore training management tools.',
  },
  {
    stepName: 'nav_products_apex_k9',
    tourAnchor: 'nav-products-apex-k9',
    title: 'APEX K9™',
    content: 'Open K9 operations, training, and field tools when enabled for your agency.',
  },
  {
    stepName: 'nav_products_apt_studio',
    tourAnchor: 'nav-products-apt-studio',
    title: 'APT Studio',
    content: 'Launch branded training websites with custom domains and syndicated APT content.',
  },
  {
    stepName: 'nav_products_chatapt',
    tourAnchor: 'nav-products-chatapt',
    title: 'ChatAPT',
    content: 'Connect with customers via in-app chat on iOS and Android, with optional AI Administrative Assistant.',
  },
  {
    stepName: 'nav_products_americon',
    tourAnchor: 'nav-products-americon',
    title: AMERICON_MARK,
    content: 'Run white-label conference portals with registration, check-in, and archives on your domain.',
  },
  {
    stepName: 'nav_products_myapt',
    tourAnchor: 'nav-products-myapt',
    title: 'MyAPT',
    content: 'Learner training app for iOS and Android—check-in, tasks, grades, and class chat.',
  },
  {
    stepName: 'nav_products_teachapt',
    tourAnchor: 'nav-products-teachapt',
    title: 'TeachAPT',
    content: 'Instructor classroom app for iOS and Android—attendance, roster, gradebook, and materials.',
  },
];

const ABOUT_DROPDOWN_ITEMS: ReadonlyArray<{
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
}> = [
  {
    stepName: 'nav_about_us_page',
    tourAnchor: 'nav-about-us-about-us',
    title: 'About Us',
    content: 'Learn about American Police Training and our mission.',
  },
  {
    stepName: 'nav_about_contact',
    tourAnchor: 'nav-about-us-contact',
    title: 'Contact',
    content: 'Reach our team for support or partnership inquiries.',
  },
  {
    stepName: 'nav_about_privacy',
    tourAnchor: 'nav-about-us-privacy-policy',
    title: 'Privacy Policy',
    content: 'Review how we handle and protect your data.',
  },
  {
    stepName: 'nav_about_terms',
    tourAnchor: 'nav-about-us-terms-of-service',
    title: 'Terms of Service',
    content: 'Read the terms governing use of the platform.',
  },
];

const USER_MENU_ITEMS: ReadonlyArray<{
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
}> = [
  {
    stepName: 'nav_user_dashboard',
    tourAnchor: 'nav-user-dashboard',
    title: 'Dashboard',
    content: 'Open your role-based dashboard for events and account tools.',
  },
  {
    stepName: 'nav_user_notifications',
    tourAnchor: 'nav-user-notifications',
    title: 'Notifications',
    content: 'Review alerts and system notifications in one place.',
  },
  {
    stepName: 'nav_user_messages',
    tourAnchor: 'nav-user-messages',
    title: 'Messages',
    content: 'Read and send messages with instructors and coordinators.',
  },
  {
    stepName: 'nav_user_my_blogs',
    tourAnchor: 'nav-user-my-blogs',
    title: 'My Blogs',
    content: 'Manage blog posts you author on the platform.',
  },
  {
    stepName: 'nav_user_my_favorites',
    tourAnchor: 'nav-user-my-favorites',
    title: 'My Favorites',
    content: 'Quick access to events and content you saved.',
  },
  {
    stepName: 'nav_user_profile_settings',
    tourAnchor: 'nav-user-profile-and-settings',
    title: 'Profile & Settings',
    content: 'Update your profile, preferences, and account settings.',
  },
  {
    stepName: 'nav_user_logout',
    tourAnchor: 'nav-user-logout',
    title: 'Sign out',
    content: 'End your session securely when you are finished.',
  },
];

/** Left-to-right header order; visibility filters guest vs authenticated layouts. */
export const NAV_REFERENCE_STEP_SPECS: readonly NavReferenceStepSpec[] = [
  {
    stepName: 'nav_mobile',
    tourAnchor: 'nav-mobile-menu',
    useStableAnchor: true,
    title: 'Mobile menu',
    content:
      'On smaller screens, open the menu to reach the same links as the desktop navigation bar.',
    visibility: 'mobile',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_logo',
    tourAnchor: 'nav-logo',
    useStableAnchor: true,
    title: 'APT logo',
    content: 'Return to the APT home page from the logo anywhere on the site.',
    visibility: 'always',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_home',
    tourAnchor: 'nav-home',
    useStableAnchor: true,
    title: 'Home',
    content: 'Open the home page from the main navigation bar.',
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_tac_shop',
    tourAnchor: 'nav-tac-shop',
    title: 'Tac-Shop',
    content: 'Browse tactical gear and training supplies.',
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_products',
    tourAnchor: 'nav-products-button',
    useStableAnchor: true,
    title: 'Products',
    content: `Open the products menu for TactiCore, APEX K9, APT Studio (with ChatAPT), and ${AMERICON_MARK}.`,
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  ...PRODUCTS_DROPDOWN_ITEMS.map(
    (item): NavReferenceStepSpec => ({
      ...item,
      visibility: 'desktop',
      beforeAction: 'openNavProductsDropdown',
      placement: 'bottom',
      isDropdownItem: true,
    }),
  ),
  {
    stepName: 'nav_browse',
    tourAnchor: 'nav-browse-button',
    useStableAnchor: true,
    title: 'Browse',
    content: 'Open the browse menu for events, agencies, venues, and more.',
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  ...BROWSE_DROPDOWN_ITEMS.map(
    (item): NavReferenceStepSpec => ({
      ...item,
      visibility: 'desktop',
      beforeAction: 'openNavBrowseDropdown',
      placement: 'bottom',
      isDropdownItem: true,
    }),
  ),
  {
    stepName: 'nav_blog',
    tourAnchor: 'nav-blog',
    title: 'Blog',
    content: 'Read news, training tips, and community updates.',
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_travel',
    tourAnchor: 'nav-travel',
    title: 'Travel',
    content: 'Find travel resources and lodging for out-of-area training.',
    visibility: 'desktop',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_leaderboard',
    tourAnchor: 'nav-leaderboard',
    title: 'Leaderboard',
    content: 'See training engagement rankings when enabled for your account.',
    visibility: 'auth',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_about',
    tourAnchor: 'nav-about-us-button',
    useStableAnchor: true,
    title: 'About Us',
    content: 'Company information, contact options, and policies for visitors.',
    visibility: 'guest',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  ...ABOUT_DROPDOWN_ITEMS.map(
    (item): NavReferenceStepSpec => ({
      ...item,
      visibility: 'guest',
      beforeAction: 'openNavAboutDropdown',
      placement: 'bottom',
      isDropdownItem: true,
    }),
  ),
  {
    stepName: 'nav_theme_toggle',
    tourAnchor: 'nav-theme-toggle',
    title: 'Theme',
    content: 'Switch between light and dark mode for comfortable viewing.',
    visibility: 'always',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_notifications',
    tourAnchor: 'notifications',
    useStableAnchor: true,
    title: 'Notifications',
    content: 'Review alerts and messages in one place.',
    visibility: 'auth',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  {
    stepName: 'nav_user_menu',
    tourAnchor: 'nav-user-menu',
    useStableAnchor: true,
    title: 'User menu',
    content: 'Account shortcuts, settings, and sign out.',
    visibility: 'auth',
    beforeAction: 'closeNavDropdownsAndScroll',
    placement: 'bottom',
  },
  ...USER_MENU_ITEMS.map(
    (item): NavReferenceStepSpec => ({
      ...item,
      visibility: 'auth',
      beforeAction: 'openNavUserMenu',
      placement: 'bottom',
      isDropdownItem: true,
    }),
  ),
];

export function getNavReferenceStepSpec(
  stepName: string,
): NavReferenceStepSpec | undefined {
  return NAV_REFERENCE_STEP_SPECS.find((spec) => spec.stepName === stepName);
}

export interface BuiltNavReferenceStep {
  stepName: string;
  target: string;
  placement: TourPlacement;
  title: string;
  content: string;
  beforeAction?: NavReferenceBeforeAction;
}

export function buildNavReferenceTourSteps(
  copyByStep: Record<string, { title?: string; content?: string }> = {},
): BuiltNavReferenceStep[] {
  return NAV_REFERENCE_STEP_SPECS.map((spec) => {
    const copy = copyByStep[spec.stepName];
    return {
      stepName: spec.stepName,
      target: navReferenceTarget(spec.tourAnchor, spec.useStableAnchor),
      placement: spec.placement ?? 'bottom',
      title: copy?.title ?? spec.title,
      content: copy?.content ?? spec.content,
      beforeAction: spec.beforeAction,
    };
  });
}
